export const feedHealthStatus = (feedHealth) => feedHealth?.status || null;

export const SNAPSHOT_STALE_AFTER_MS = 4 * 60 * 1000;
export const SNAPSHOT_FUTURE_TOLERANCE_MS = 2 * 60 * 1000;
const VALID_FEED_HEALTH_STATUSES = new Set([
  "healthy",
  "degraded",
  "unavailable",
  "recovering",
  "idle",
]);
const PARTNER_FAILURE_REASONS = new Set([
  "vehicle_fetch_failed",
  "malformed_vehicle_activity",
  "empty_vehicle_activity",
  "excessive_invalid_records",
  "low_vehicle_coverage",
  "sudden_vehicle_collapse",
]);

export const effectiveFeedHealth = (payload, nowMs = Date.now()) => {
  if (!payload || !Object.prototype.hasOwnProperty.call(payload, "feedHealth")) {
    return null;
  }
  if (
    !payload.feedHealth ||
    typeof payload.feedHealth !== "object" ||
    typeof payload.feedHealth.status !== "string" ||
    payload.feedHealth.status.length === 0 ||
    !VALID_FEED_HEALTH_STATUSES.has(payload.feedHealth.status)
  ) {
    return { status: "unavailable", reason: "snapshot_invalid" };
  }
  if (!isLiveVehicleData(payload.feedHealth)) return payload.feedHealth;
  const generatedAtMs = Date.parse(payload.generatedAt);
  if (
    !Number.isFinite(generatedAtMs) ||
    nowMs - generatedAtMs > SNAPSHOT_STALE_AFTER_MS ||
    generatedAtMs - nowMs > SNAPSHOT_FUTURE_TOLERANCE_MS
  ) {
    return { status: "unavailable", reason: "snapshot_stale" };
  }
  return payload.feedHealth;
};

export const isLiveVehicleData = (feedHealth) => {
  const status = feedHealthStatus(feedHealth);
  return status === null || status === "healthy" ||
    (["degraded", "unavailable"].includes(status) && feedHealth?.dataSource === "vehicle");
};

export const isScheduleOnly = (feedHealth) => {
  const status = feedHealthStatus(feedHealth);
  return !isLiveVehicleData(feedHealth) && status !== "idle";
};

export const isLastKnownVehicleData = (feedHealth) =>
  isScheduleOnly(feedHealth) && feedHealth?.displayMode === "last_known_vehicle";

export const isIdleService = (feedHealth) =>
  feedHealthStatus(feedHealth) === "idle";

export const isPartnerFeedFailure = (feedHealth) =>
  !(feedHealth?.status && isLiveVehicleData(feedHealth)) && (PARTNER_FAILURE_REASONS.has(feedHealth?.reason) ||
  (feedHealth?.reason === "fleet_positions_frozen" &&
    feedHealth?.positionIssueClassification === "partner_feed_suspected"));

export const hasManagedFeedWarning = (feedHealth) =>
  isPartnerFeedFailure(feedHealth) ||
  feedHealth?.reason === "fleet_positions_frozen" ||
  feedHealth?.status === "recovering";

export const selectDisplayTrains = (payload) => {
  const liveTrains = Array.isArray(payload?.trains) ? payload.trains : [];
  if (!isScheduleOnly(payload?.feedHealth)) return liveTrains;
  if (isLastKnownVehicleData(payload?.feedHealth)) {
    return liveTrains.map((train) => ({
      ...train,
      liveDataAvailable: false,
    }));
  }
  if (Array.isArray(payload?.scheduledTrains)) return payload.scheduledTrains;
  if (["snapshot_stale", "snapshot_invalid"].includes(payload?.feedHealth?.reason)) {
    return [];
  }
  // A short-lived rollout producer put schedule rows in `trains`. Accept only
  // rows that explicitly deny live status; never treat an arbitrary train row
  // as safe fallback merely because the top-level health is non-healthy.
  return liveTrains.filter((train) => train?.liveDataAvailable === false);
};

const frozenLocationDuration = (feedHealth) => {
  const checkedAtMs = Date.parse(feedHealth?.checkedAt);
  const lastHealthyAtMs = Date.parse(feedHealth?.lastHealthyAt);
  if (
    !Number.isFinite(checkedAtMs) ||
    !Number.isFinite(lastHealthyAtMs) ||
    checkedAtMs < lastHealthyAtMs
  ) {
    return null;
  }
  return Math.max(2, Math.floor((checkedAtMs - lastHealthyAtMs) / 60_000));
};

const frozenLocationMessage = (feedHealth) => {
  const minutes = frozenLocationDuration(feedHealth);
  return minutes === null
    ? "Train locations have not changed recently."
    : `Train locations have not changed in ${minutes} minutes.`;
};

const serverFeedHealthWarning = (feedHealth, language) => {
  const preferredLanguage = String(
    language || (typeof navigator !== "undefined" ? navigator.language : "en")
  ).toLowerCase();
  if (
    preferredLanguage.startsWith("es") &&
    typeof feedHealth?.warning_es === "string" &&
    feedHealth.warning_es.trim()
  ) {
    return feedHealth.warning_es;
  }
  if (typeof feedHealth?.warning === "string" && feedHealth.warning.trim()) {
    return feedHealth.warning;
  }
  return null;
};

export const feedHealthMessage = (feedHealth, language) => {
  const serverWarning = serverFeedHealthWarning(feedHealth, language);
  if (serverWarning) return serverWarning;

  if (isLastKnownVehicleData(feedHealth)) {
    return "Live train data is temporarily unavailable.";
  }
  if (isPartnerFeedFailure(feedHealth)) {
    return "Live tracking is unavailable. Estimated locations are based on the timetable, not live train data.";
  }
  if (feedHealth?.reason === "fleet_positions_frozen") {
    return `${frozenLocationMessage(feedHealth)} Scheduled service is being shown.`;
  }
  if (feedHealth?.reason === "website_fetch_failed") {
    return "Live train data could not be refreshed. Please try again shortly.";
  }
  if (feedHealth?.reason === "snapshot_stale") {
    return "Live train data is stale and has been hidden. Please try again shortly.";
  }
  if (feedHealth?.reason === "snapshot_invalid") {
    return "Live train data could not be verified and has been hidden. Please try again shortly.";
  }
  if (feedHealth?.reason === "schedule_unavailable") {
    return "Live train data and the scheduled fallback are temporarily unavailable.";
  }
  return "Live train data is temporarily unavailable, so scheduled service is being shown.";
};
